import React from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion } from 'framer-motion';

import ClientLayout from '../../../components/layout/ClientLayout';
import PageWrapper from '../../../components/layout/PageWrapper';
import Table from '../../../components/ui/Table';
import Spinner from '../../../components/ui/Spinner';
import Alert from '../../../components/ui/Alert';
import Card from '../../../components/ui/Card';
import { useFirestoreQuery } from '../../../hooks/useFirestoreQuery';
import { listarCampanhas } from '../../../api/campaigns.api';
import { ICampanha as Campanha } from '../../../types/firestore.types';

const CampaignsPage: React.FC = () => {
  const { dados: campanhas, carregando, erro } = useFirestoreQuery<Campanha>(
    'campanhas',
    [],
    { campo: 'data_fim', direcao: 'desc' }
  );

  const colunasTabela = [
    { titulo: 'Nome da Campanha', chave: 'nome' },
    { titulo: 'Descrição', chave: 'descricao' },
    { titulo: 'Cashback', chave: 'percentual_cashback' },
    { titulo: 'Valor Mínimo', chave: 'valor_minimo' },
    { titulo: 'Início', chave: 'data_inicio' },
    { titulo: 'Fim', chave: 'data_fim' },
  ];

  const formatarDadosCampanhas = (campanhasData: Campanha[]) => {
    return campanhasData.map((campanha) => ({
      ...campanha,
      percentual_cashback: `${campanha.percentual_cashback}%`,
      valor_minimo: campanha.valor_minimo ? `TC ${campanha.valor_minimo.toFixed(2).replace('.', ',')}` : 'N/A',
      data_inicio: campanha.data_inicio ? format(campanha.data_inicio.toDate(), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A',
      data_fim: campanha.data_fim ? format(campanha.data_fim.toDate(), 'dd/MM/yyyy', { locale: ptBR }) : 'N/A',
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
  };

  return (
    <ClientLayout>
      <PageWrapper>
        <motion.div
          className="flex flex-col gap-6 p-6 md:p-8 lg:p-10 bg-background text-text min-h-screen"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.h1 className="text-4xl font-extrabold text-primary mb-4" variants={itemVariants}>
            Campanhas Exclusivas
          </motion.h1>
          <motion.p className="text-lg text-secondary mb-6" variants={itemVariants}>
            Descubra as melhores ofertas e acumule mais TugasCoins!
          </motion.p>

          {carregando && (
            <motion.div
              className="flex justify-center items-center h-64"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Spinner size="lg" color="primary" />
              <p className="ml-4 text-primary text-lg">Carregando campanhas...</p>
            </motion.div>
          )}

          {erro && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Alert tipo="error" mensagem={`Erro ao carregar campanhas: ${erro.message}`} />
            </motion.div>
          )}

          {!carregando && !erro && (!campanhas || campanhas.length === 0) && (
            <motion.div variants={itemVariants}>
              <Card className="p-8 text-center bg-background-darker shadow-lg rounded-lg">
                <p className="text-xl text-secondary">
                  Nenhuma campanha disponível no momento. Fique de olho para futuras promoções!
                </p>
              </Card>
            </motion.div>
          )}

          {!carregando && !erro && campanhas && campanhas.length > 0 && (
            <motion.div variants={itemVariants}>
              <Card className="p-6 bg-background-darker shadow-lg rounded-lg">
                <h2 className="text-2xl font-bold text-accent mb-4">Campanhas Ativas</h2>
                <div className="overflow-x-auto">
                  <Table
                    colunas={colunasTabela}
                    dados={formatarDadosCampanhas(campanhas)}
                    className="min-w-full"
                  />
                </div>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </PageWrapper>
    </ClientLayout>
  );
};

export default CampaignsPage;